import { HubFeature } from '@ats/api/cm';
import { LocDef, LocDefs, LocMap, MapLocKeys } from '@cm/core/localization';

const dpRoot = 'Dashboard Report|Voucher I/O Verification';
const sharedProperties: Pick<LocDef, 'shared' | 'htmlAllowed' | 'feature' | 'portal'> = {
	shared: false,
	htmlAllowed: false,
	feature: HubFeature.ComptiaCustomDashboardReports,
	portal: 'SP',
};

const singleVoucherIdInstructions = `
<p class="mb-0">To look up a single Voucher ID Verification:</p>
<ul>
<li>Enter the Voucher ID</li>
<li>Click "Verify Voucher ID"</li>
</ul>
<p>See below for the following column headings that will be returned in the verification results.csv file:</p>
`;
const multipleVoucherIdInstructions = `
<p>
<strong>To create the Comma Separated File (CSV) input verification file:</strong>
</p>
<p>
The following column headers are recognized by the parser:
</p>
<ul>
<li>Voucher ID (required)</li>
</ul>
<p>The Voucher field is required.</p>
<p>
<strong>Return CSV file:</strong></p>
<p>After pressing the Verify Voucher IDs button, a CSV file will be returned with following columns:</p>
<ul>
<li>Voucher ID</li>
<li>Candidate Name</li>
<li>Exam Code</li>
<li>Exam Date</li>
<li>Exam Grade</li>
</ul>
<p>The following column headings will be returned in the verification results.csv file (defined as):</p>
<ul>
<li>Voucher ID&mdash;The original Voucher ID supplied.</li>
<li>Candidate Name (field will contain one of two values as noted below):</li>
<ul>
<li>The candidate's name if the Voucher ID was found.</li>
<li>Not Found&mdash;The Voucher ID does not exist in our system.</li>
</ul>
<li>Exam Code&mdash;Indicates exam taken.</li>
<li>Exam Date&mdash;Date the exam was taken.</li>
<li>Exam Grade&mdash;Pass/Fail</li>
</ul>
<p>The following information will be returned in the verification <strong>totals.txt</strong> file:</p>
<p>Info:</p>
<ul>
<li>Login ID&mdash;The Login ID is the User name that was logged in when the verification was done.</li>
<li>Name&mdash;The Name is the User's first and last name.</li>
<li>Company&mdash;The Company is the name of the organization the user is associated with (employer).</li>
<li>Date&mdash;The Date is when the data was requested.</li>
<li>Data range selected&mdash;The Date range selected is the (optional) From/To dates entered.</li>
</ul>
<p>Totals:</p>
<ul>
<li>Candidates&mdash;The Candidates is the number of candidates that used the voucher. The Value will be one unless the voucher provided is a Site license 100 use voucher.</li>
<li>Passed&mdash;The Passed value is the number of candidates who passed the exam.</li>
<li>Failed&mdash;The Failed value is the number of candidates who failed the exam.</li>
<li>% Passed&mdash;The % passed is the % of candidates who passed the exam.</li>
</ul>
`;

export type SPVoucherIOVerificationLocNames =
	| 'SPVoucherIOVerificationPageTitle'
	| 'SPVoucherIOVerificationPageHeading'
	| 'SPVoucherIOVerificationDateRangeSection'
	| 'SPVoucherIOVerificationDateRangeHeading'
	| 'SPVoucherIOVerificationSingleSection'
	| 'SPVoucherIOVerificationSingleInstructions'
	| 'SPVoucherIOVerificationSingleButton'
	| 'SPVoucherIOVerificationSingleVoucherIdLabel'
	| 'SPVoucherIOVerificationMultipleVoucherIdRequiredError'
	| 'SPVoucherIOVerificationMultipleSection'
	| 'SPVoucherIOVerificationMultipleInstructions'
	| 'SPVoucherIOVerificationMultipleButton'
	| 'SPVoucherIOVerificationFromDateLabel'
	| 'SPVoucherIOVerificationToDateLabel'
	| 'SPVoucherIOVerificationFromDateMaxError'
	| 'SPVoucherIOVerificationToDateMinError'
	| 'SPVoucherIOVerificationCsvUploadError'
	| 'SPVoucherIOVerificationSuccess';

export const spVoucherIOVerificationLocs: LocDefs<SPVoucherIOVerificationLocNames> = {
	SPVoucherIOVerificationPageTitle: {
		defaultValue: 'Voucher I/O Verification',
		description: 'Page title for the voucher I/O verification page.',
		displayPath: `${dpRoot}|Page Title`,
		displayType: 'Title',
		...sharedProperties,
	},
	SPVoucherIOVerificationPageHeading: {
		defaultValue: 'Voucher I/O Verification',
		description: 'Page Heading for the voucher I/O verification page.',
		displayPath: `${dpRoot}|Page Heading`,
		displayType: 'Title',
		...sharedProperties,
	},
	SPVoucherIOVerificationDateRangeSection: {
		defaultValue: 'Voucher I/O Verification Tool',
		description: 'Section title for the voucher I/O verification page.',
		displayPath: `${dpRoot}|Date Range Section`,
		displayType: 'Title',
		...sharedProperties,
	},
	SPVoucherIOVerificationDateRangeHeading: {
		defaultValue: 'Date Range (optional)',
		description: 'Date range heading for the date form section.',
		displayPath: `${dpRoot}|Date Range Heading`,
		displayType: 'Content',
		...sharedProperties,
	},
	SPVoucherIOVerificationSingleSection: {
		defaultValue: 'Single Voucher ID Verification',
		description: 'Section title for the single voucher I/O verification section.',
		displayPath: `${dpRoot}|Single Voucher I/O Verification Section`,
		displayType: 'Title',
		...sharedProperties,
	},
	SPVoucherIOVerificationSingleVoucherIdLabel: {
		defaultValue: 'Voucher ID',
		description: 'Label for the voucher ID form field.',
		displayPath: `${dpRoot}|Single Voucher I/O Verification Voucher ID Label`,
		displayType: 'Label',
		...sharedProperties,
	},
	SPVoucherIOVerificationMultipleVoucherIdRequiredError: {
		defaultValue: 'Voucher ID is required.',
		description: 'Error message for the voucher ID form field.',
		displayPath: `${dpRoot}|Multiple Voucher I/O Verification Voucher ID Required Error`,
		displayType: 'Error',
		...sharedProperties,
	},
	SPVoucherIOVerificationSingleButton: {
		defaultValue: 'Verify Voucher ID',
		description: 'Button text for the single voucher I/O verification section.',
		displayPath: `${dpRoot}|Single Voucher I/O Verification Button`,
		displayType: 'Button',
		...sharedProperties,
	},
	SPVoucherIOVerificationMultipleSection: {
		defaultValue: 'Multiple Voucher ID Verification',
		description: 'Section title for the multiple voucher I/O verification section.',
		displayPath: `${dpRoot}|Multiple Voucher I/O Verification Section`,
		displayType: 'Title',
		...sharedProperties,
	},
	SPVoucherIOVerificationMultipleButton: {
		defaultValue: 'Verify Voucher IDs',
		description: 'Button text for the multiple voucher I/O verification section.',
		displayPath: `${dpRoot}|Multiple Voucher I/O Verification Button`,
		displayType: 'Button',
		...sharedProperties,
	},
	SPVoucherIOVerificationFromDateLabel: {
		defaultValue: 'From Date',
		description: 'Label for the from date form field.',
		displayPath: `${dpRoot}|From Date Label`,
		displayType: 'Label',
		...sharedProperties,
	},
	SPVoucherIOVerificationToDateLabel: {
		defaultValue: 'To Date',
		description: 'Label for the to date form field.',
		displayPath: `${dpRoot}|To Date Label`,
		displayType: 'Label',
		...sharedProperties,
	},
	SPVoucherIOVerificationSingleInstructions: {
		defaultValue: singleVoucherIdInstructions,
		description:
			'Instructions for the single voucher ID section of the voucher I/O verification page.',
		displayPath: `${dpRoot}|Single Voucher ID Instructions`,
		displayType: 'Content',
		shared: false,
		htmlAllowed: true,
		feature: HubFeature.ComptiaCustomDashboardReports,
		portal: 'SP',
	},
	SPVoucherIOVerificationMultipleInstructions: {
		defaultValue: multipleVoucherIdInstructions,
		description:
			'Instructions for the multiple voucher IDs section of the voucher I/O verification page.',
		displayPath: `${dpRoot}|Multiple Voucher IDs Instructions`,
		displayType: 'Content',
		shared: false,
		htmlAllowed: true,
		feature: HubFeature.ComptiaCustomDashboardReports,
		portal: 'SP',
	},
	SPVoucherIOVerificationFromDateMaxError: {
		defaultValue: 'From date must be before to date.',
		description: 'Error message for the from date form field.',
		displayPath: `${dpRoot}|From Date Max Error`,
		displayType: 'Error',
		...sharedProperties,
	},
	SPVoucherIOVerificationToDateMinError: {
		defaultValue: 'To date must be after from date.',
		description: 'Error message for the to date form field.',
		displayPath: `${dpRoot}|To Date Min Error`,
		displayType: 'Error',
		...sharedProperties,
	},
	SPVoucherIOVerificationCsvUploadError: {
		defaultValue: 'Error parsing CSV file.',
		description: 'Error message for the CSV upload error.',
		displayPath: `${dpRoot}|CSV Upload Error`,
		displayType: 'Error',
		...sharedProperties,
	},
	SPVoucherIOVerificationSuccess: {
		defaultValue: 'Voucher(s) verified successfully. Your download should start shortly.',
		description: 'Success message when verifying voucher(s).',
		displayPath: `${dpRoot}|Verification Success`,
		displayType: 'Notification',
		...sharedProperties,
	},
};

export type SPVoucherIOVerificationLocType = LocMap<SPVoucherIOVerificationLocNames>;

export const VOUCHER_IO_VERIFICATION_LOC_KEYS = MapLocKeys<SPVoucherIOVerificationLocNames>(
	spVoucherIOVerificationLocs
);
